import type { PairingCode } from '@obv/api-client';
import { applyTheme, isStandalone } from '@obv/ui';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useHideSplash } from '@/lib/splash';

const POLL_MS = 3000;

/** Shown on a fresh iPad: displays a code that an admin enters on their phone. */
export function PairScreen({ onPaired }: { onPaired: () => void }) {
  useHideSplash();
  const rootRef = useRef<HTMLDivElement>(null);
  const [code, setCode] = useState<PairingCode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useLayoutEffect(() => {
    if (rootRef.current) applyTheme(rootRef.current, 'colorful', true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.pairing
      .create('Living room iPad')
      .then((c) => !cancelled && setCode(c))
      .catch(() => !cancelled && setError('Can’t reach OurBeachVila. Check the Wi-Fi — retrying…'));
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setAttempt((a) => a + 1), 10_000);
      return () => clearTimeout(t);
    }
    if (!code) return;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      try {
        const s = await api.pairing.status(code.code, code.poll_token);
        if (s.status === 'claimed') {
          if (await api.http.refresh()) onPaired();
          return;
        }
      } catch (e) {
        const c = (e as { code?: string }).code;
        if (c === 'pairing.code_expired' || c === 'pairing.code_unknown') {
          setCode(null);
          setAttempt((a) => a + 1);
          return;
        }
      }
      timer = setTimeout(poll, POLL_MS);
    };
    let timer = setTimeout(poll, POLL_MS);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [code, error, onPaired]);

  return (
    <div ref={rootRef} className="display-root flex flex-col items-center justify-center text-center">
      <div className="flex h-[52px] w-[72px] items-center justify-center rounded-[14px] font-serif text-[22px]" style={{ background: 'var(--ink)', color: 'var(--onInk)' }}>
        OBV
      </div>
      <h1 className="mt-6 font-serif text-[40px] font-normal">Let’s set up this display.</h1>
      <p className="mt-3 max-w-[520px] text-[16px] leading-[1.5] text-muted">
        On your phone, open OurBeachVila → <b>Pair a display</b> and enter this code.
      </p>
      <div className="mt-8 flex" aria-live="polite">
        {(code ? code.code : '······').split('').map((ch, i) => (
          <div key={i} className="card mx-[6px] flex h-[84px] w-[64px] items-center justify-center font-serif text-[44px]" style={{ background: 'var(--card)' }}>
            {ch}
          </div>
        ))}
      </div>
      <p className="mt-6 text-[14px] text-muted">{error || (code ? 'Waiting for your phone… the code refreshes automatically.' : 'Getting a code…')}</p>
      {/iPad|iPhone|iPod/.test(navigator.userAgent) && !isStandalone() ? (
        // iOS keeps Home Screen web apps' cookies apart from Safari's, so pairing here would have
        // to be repeated after adding the app to the Home Screen.
        <p className="mt-6 max-w-[520px] rounded-[14px] px-4 py-3 text-[14px] leading-[1.45]" style={{ background: 'var(--soft)' }}>
          <b>Tip:</b> tap Share → <b>Add to Home Screen</b> first, then open OBV from its icon and pair there. It runs full screen, and pairing in Safari won’t carry over.
        </p>
      ) : null}
      <p className="mt-2 text-[13px] text-muted">{window.location.host}/app</p>
    </div>
  );
}
